// "X vs Y" programmatic SEO comparison pages.
// Each entry powers one /a-vs-b route via ComparisonPage.jsx.

// Iter-196.DD — 30 carrier-vs-carrier insurance comparisons
// (geico-vs-progressive, state-farm-vs-allstate, etc.). Generated
// programmatically from the carrier registry + per-pair editorial
// data — the largest unclaimed search-volume gap identified in the
// iter-196.BB topical-authority analysis.
import { generateCarrierVsComparisons } from "./carrierVsRegistry";

// Iter-196.R (M8) — Decision-comparison lane groupings. Surfaces
// the flat /compare grid into 3 editorial lanes ("Most-compared
// this week", "Editor picks", "Underrated picks") when the user
// hasn't applied a category filter. Pattern borrowed from
// NerdWallet's compare-index — converts intent-stage browsers
// who don't yet know which decision matters most for them.
// Lane assignments are editorial (curated, not algorithmic).
// Each slug appears in exactly one lane. Comparisons not listed
// here fall back to the legacy ungrouped tail row.
export const COMPARISON_LANES = [
  {
    id: "most-compared",
    label: "Most-compared this week",
    sub: "The decisions our visitors land on most often when they hit /compare for the first time.",
    slugs: [
      "lease-vs-finance",
      "refinance-vs-keep-loan",
      "full-coverage-vs-liability-only",
      "new-vs-used",
    ],
  },
  {
    id: "editor-picks",
    label: "Editor picks",
    sub: "Higher-judgment decisions where the better answer depends on your numbers, not the default.",
    slugs: [
      "short-term-vs-long-term-auto-loan",
      "high-vs-low-deductible-insurance",
      "manufacturer-vs-third-party-warranty",
      "dealer-financing-vs-credit-union",
    ],
  },
  {
    id: "underrated",
    label: "Underrated picks",
    sub: "Niche but high-impact decisions most comparison sites skip — pick one and save real money.",
    slugs: [
      "bank-vs-online-refinance",
      "refinance-vs-trade-in",
      "state-minimum-vs-full-coverage",
      "buy-here-pay-here-vs-bank-loan",
    ],
  },
];

export const COMPARISONS = [
  {
    slug: "lease-vs-finance",
    title: "Lease vs. Finance",
    h1: "Lease vs. Finance a Car: Which Saves You More in 2026?",
    description:
      "Side-by-side breakdown of leasing vs. financing — total cost, monthly payments, mileage rules, and the driver profile each option fits best.",
    category: "Buying",
    read_time: "7 min read",
    related_tool: "car-affordability-calculator",
    a: {
      name: "Lease",
      tagline: "Drive new, walk away in 3 years.",
      monthly: "$390 – $620",
      term: "24 – 39 months typical",
      ownership: "You never own the car",
      mileage: "10,000 – 15,000 mi/yr cap (overage fees apply)",
      maintenance: "Usually under bumper-to-bumper warranty",
      upfront: "First payment + acquisition fee (~$700) + taxes",
      end_of_term: "Return, buy out at residual, or trade",
      pros: [
        "Lower monthly payment for the same car",
        "Always under warranty",
        "Drive a new car every 2–3 years",
        "Easier to budget — no surprise repairs",
      ],
      cons: [
        "You build zero equity",
        "Mileage caps — overage typically $0.15–$0.30/mi",
        "Wear & tear charges at turn-in",
        "Hardest to exit early without penalty",
      ],
    },
    b: {
      name: "Finance (Buy)",
      tagline: "Pay it off. Own it. Drive it forever.",
      monthly: "$580 – $810",
      term: "48 – 84 months typical",
      ownership: "Yours when paid off",
      mileage: "Unlimited — but high miles hurt resale",
      maintenance: "Yours after factory warranty ends (3–5 yr)",
      upfront: "Down payment + tax/title/registration",
      end_of_term: "Drive it 5–15 more years for free",
      pros: [
        "Own the asset at payoff",
        "Drive as much as you want",
        "Customize, modify, sell anytime",
        "Lowest long-term cost if you keep it",
      ],
      cons: [
        "Higher monthly payment",
        "Depreciation hits hardest in year 1",
        "You eat all repair costs after warranty",
        "Negative equity risk if you sell early",
      ],
    },
    table: [
      { feature: "Typical monthly payment", a: "$390–$620", b: "$580–$810" },
      { feature: "Term length", a: "24–39 months", b: "48–84 months" },
      { feature: "Ownership at end", a: "None", b: "Full title" },
      { feature: "Mileage limit", a: "10–15k/yr", b: "Unlimited" },
      { feature: "Repairs/maintenance", a: "Under warranty", b: "You pay after warranty" },
      { feature: "Customization allowed", a: "No", b: "Yes" },
      { feature: "Build equity", a: "No", b: "Yes" },
      { feature: "Easy to exit early", a: "Hard", b: "Sell or trade anytime" },
    ],
    winner: {
      lease: "You drive <12k mi/yr, want a new car every 3 years, value low payments, or write the lease off as a business expense.",
      buy: "You drive a lot, keep cars 5+ years, want the lowest 10-year cost, or plan to modify the vehicle.",
    },
    faqs: [
      {
        q: "Is leasing or buying cheaper over 10 years?",
        a: "Buying — by roughly $8,000–$15,000 over a decade if you keep the car. Leasing only wins when you'd trade-in every 3 years anyway.",
      },
      {
        q: "Can I lease with bad credit?",
        a: "Leasing typically requires a higher FICO score than financing (660+ minimum at most captives). Below that, financing or buy-here-pay-here are more realistic.",
      },
      {
        q: "What is a lease buyout?",
        a: "An option in your lease contract to buy the car at the predetermined residual value when the lease ends. With 2022–2024 leases, residuals were often below market — buyouts can be a great deal.",
      },
    ],
  },
  {
    slug: "refinance-vs-trade-in",
    title: "Refinance vs. Trade In",
    h1: "Refinance vs. Trade In: Which Cuts Your Car Payment Faster?",
    description:
      "You want a lower car payment — but should you refinance the loan you have or trade in for a cheaper car? The honest math, side-by-side.",
    category: "Refinance",
    read_time: "6 min read",
    related_tool: "auto-loan-refinance-calculator",
    a: {
      name: "Refinance",
      tagline: "Same car, better loan.",
      monthly: "Cut $40 – $180/mo typical",
      term: "Reset to 36 / 48 / 60 / 72 months",
      ownership: "You keep your car",
      mileage: "N/A",
      maintenance: "Same as before",
      upfront: "Usually $0 – $250 (title transfer)",
      end_of_term: "Owned free and clear",
      pros: [
        "Keep the car you already know",
        "Lower APR if credit improved",
        "Soft credit pull to shop",
        "Skip a payment is common during refi",
      ],
      cons: [
        "Only works if rates dropped or credit rose",
        "Resetting the term extends total interest",
        "Some lenders charge prepay penalties",
        "Underwater loans rarely refinance",
      ],
    },
    b: {
      name: "Trade-In",
      tagline: "Different car, smaller loan.",
      monthly: "Cut $80 – $400/mo typical",
      term: "New loan on a cheaper vehicle",
      ownership: "New car, fresh loan",
      mileage: "Unlimited",
      maintenance: "Often newer = under warranty",
      upfront: "Down payment + tax/title/reg + any negative equity rolled in",
      end_of_term: "Pay off the new loan",
      pros: [
        "Bigger payment reduction possible",
        "Newer vehicle, fewer repairs",
        "Use positive equity as down payment",
        "Fresh warranty coverage",
      ],
      cons: [
        "Negative equity rolls into the new loan",
        "Hard credit pull (multiple in 14 days = 1)",
        "Tax/title/reg adds $1,000–$3,000",
        "Dealer markups can erase savings",
      ],
    },
    table: [
      { feature: "Keep your car", a: "Yes", b: "No" },
      { feature: "Lower payment", a: "Yes ($40–$180)", b: "Yes ($80–$400)" },
      { feature: "Closing costs", a: "$0–$250", b: "$1,000–$3,000+" },
      { feature: "Credit pull", a: "Soft pull to shop", b: "Hard pull required" },
      { feature: "Works if underwater", a: "Rarely", b: "Yes, but rolls in negative equity" },
      { feature: "Time to complete", a: "1–7 days", b: "1–3 days at dealer" },
      { feature: "Resets warranty/age clock", a: "No", b: "Yes (newer car)" },
    ],
    winner: {
      refinance: "Your credit jumped 50+ points, market rates dropped 1%+, or your original lender padded the rate at the dealership.",
      tradein: "You're underwater on a depreciating model, repair bills are climbing, or you'd save more on insurance + maintenance by switching vehicles.",
    },
    faqs: [
      {
        q: "Will refinancing hurt my credit?",
        a: "Shopping is a soft pull. The final refinance is a hard pull, costing ~5 points temporarily. Multiple hard pulls inside a 14-day window count as one inquiry.",
      },
      {
        q: "Should I refinance if I'm upside down?",
        a: "Most lenders won't refinance a loan where the balance exceeds the car's value (LTV > 125%). In that case, focus on paying down principal first or wait for the gap to shrink.",
      },
      {
        q: "Can I refinance immediately after buying?",
        a: "Some lenders require 60–90 days of payment history before refinancing. After that, you're free to shop — and many borrowers do, especially after dealer rate markups.",
      },
    ],
  },
  {
    slug: "new-vs-used",
    title: "New vs. Used Car",
    h1: "New vs. Used Cars: The 2026 Cost Breakdown",
    description:
      "Buying new vs. used isn't just sticker price. Compare depreciation, financing rates, insurance, and total 5-year cost.",
    category: "Buying",
    read_time: "6 min read",
    related_tool: "car-affordability-calculator",
    a: {
      name: "New Car",
      tagline: "Factory-fresh, full warranty, top APR rates.",
      monthly: "$680 – $920",
      term: "48 – 72 months typical",
      ownership: "Yours at payoff",
      mileage: "Unlimited",
      maintenance: "Bumper-to-bumper warranty 3–5 yr",
      upfront: "10–20% down, tax/title/reg",
      end_of_term: "5–10 yr old vehicle worth ~35–45% of MSRP",
      pros: [
        "Lowest APRs (5–7% for 700+ credit)",
        "Latest safety tech (auto-brake, lane assist)",
        "Full manufacturer warranty",
        "First-owner pride + customization options",
      ],
      cons: [
        "Loses ~20% of value the first year",
        "Insurance premiums run 15–25% higher",
        "Higher sales tax base",
        "Dealer F&I markup pressure",
      ],
    },
    b: {
      name: "Used Car",
      tagline: "Let someone else eat the depreciation.",
      monthly: "$340 – $620",
      term: "36 – 72 months typical",
      ownership: "Yours at payoff",
      mileage: "Unlimited",
      maintenance: "Often no factory warranty (or CPO)",
      upfront: "10–20% down, lower tax base",
      end_of_term: "Depreciation curve flattens after year 5",
      pros: [
        "2–3 year-old cars run 30–40% cheaper",
        "Slower depreciation = better resale",
        "Lower insurance premiums",
        "CPO programs offer 7-yr powertrain coverage",
      ],
      cons: [
        "Higher APRs (typically 1–3% more)",
        "Older safety/tech features",
        "Unknown maintenance history (non-CPO)",
        "Vehicle history risk (flood, accidents)",
      ],
    },
    table: [
      { feature: "Sticker price (mid-size SUV)", a: "$38,000", b: "$24,000 (3-yr-old)" },
      { feature: "Typical APR (720 FICO)", a: "5.9%", b: "7.2%" },
      { feature: "5-yr depreciation", a: "~50%", b: "~30%" },
      { feature: "Annual insurance", a: "$1,950", b: "$1,580" },
      { feature: "Warranty at purchase", a: "Full factory", b: "Limited or none (CPO = 1–2 yr)" },
      { feature: "Sales tax base", a: "Higher", b: "Lower" },
      { feature: "Reliability risk", a: "Lowest", b: "Depends on history" },
    ],
    winner: {
      new: "You drive 15k+ mi/yr, value latest safety tech, plan to keep the car 8+ years, or qualify for 0% manufacturer incentives.",
      used: "You want the lowest 5-year total cost, plan to pay off fast, and accept some maintenance risk — especially with a CPO purchase.",
    },
    faqs: [
      {
        q: "Why are used car APRs higher than new?",
        a: "Lenders charge more for older collateral because resale value is harder to predict. Expect 1–3 percentage points higher on used vs. new for the same credit profile.",
      },
      {
        q: "Is buying a 1-year-old car the sweet spot?",
        a: "Often yes. A 1-year-old car typically costs 18–22% less than new but still has most of the factory warranty intact — capturing depreciation savings without losing peace of mind.",
      },
      {
        q: "Should I buy CPO?",
        a: "CPO (Certified Pre-Owned) cars cost ~$1,500–$3,000 more than a comparable non-CPO but include a multi-point inspection and extended warranty. Worth it for luxury brands with expensive repair costs.",
      },
    ],
  },
  {
    slug: "full-coverage-vs-liability-only",
    title: "Full Coverage vs. Liability Only",
    h1: "Full Coverage vs. Liability-Only Auto Insurance: Which Do You Need?",
    description:
      "Most drivers pay too much for coverage they don't need — or too little for coverage they do. The cutoff line, in real numbers.",
    category: "Insurance",
    read_time: "5 min read",
    related_tool: "auto-insurance-cost-estimator",
    a: {
      name: "Full Coverage",
      tagline: "Liability + collision + comprehensive.",
      monthly: "$135 – $215",
      term: "Annual or 6-month policy",
      ownership: "Required if you have a loan/lease",
      mileage: "N/A",
      maintenance: "Covers your car in nearly all events",
      upfront: "Deductible $250 – $2,500 typical",
      end_of_term: "Renew or shop again",
      pros: [
        "Repairs your car after at-fault crashes",
        "Comprehensive covers theft, hail, vandalism",
        "Required by every auto lender",
        "Peace of mind on newer vehicles",
      ],
      cons: [
        "30–60% more expensive than liability-only",
        "Deductible eats small claims",
        "May exceed the car's value on older models",
        "Premiums spike fast after claims",
      ],
    },
    b: {
      name: "Liability-Only",
      tagline: "Covers the other guy. Not your car.",
      monthly: "$55 – $95",
      term: "Annual or 6-month policy",
      ownership: "Legal minimum in most states",
      mileage: "N/A",
      maintenance: "Covers damage you cause to others",
      upfront: "No deductible (pays other party)",
      end_of_term: "Renew or shop again",
      pros: [
        "Cheapest legal way to drive",
        "No coverage gap when car is paid off",
        "Right call for old, low-value cars",
        "Same legal protection as full coverage",
      ],
      cons: [
        "Your car gets totaled? You pay",
        "Theft, hail, vandalism not covered",
        "Some states' minimums are dangerously low",
        "Cannot satisfy a loan/lease requirement",
      ],
    },
    table: [
      { feature: "Avg. annual premium (national)", a: "$2,000", b: "$700" },
      { feature: "Pays for your car (at-fault)", a: "Yes", b: "No" },
      { feature: "Theft / vandalism", a: "Yes", b: "No" },
      { feature: "Hail / weather damage", a: "Yes", b: "No" },
      { feature: "Required by lenders", a: "Yes", b: "No (paid-off only)" },
      { feature: "Other driver's injury / car", a: "Yes", b: "Yes" },
      { feature: "Deductible", a: "$250–$2,500", b: "None" },
    ],
    winner: {
      full: "Your car is under 10 years old, worth $4,000+, financed, or you can't easily afford to replace it out of pocket.",
      liability: "Your car is paid off, worth under $4,000, AND you have an emergency fund to replace it — the premium savings beat the coverage value.",
    },
    faqs: [
      {
        q: "When should I drop collision coverage?",
        a: "Use the 10% rule: when your annual collision + comprehensive premium exceeds 10% of the car's market value, the math stops making sense. For a $4,000 car, that's $400/year.",
      },
      {
        q: "Is liability-only legal everywhere?",
        a: "Yes — except New Hampshire, where liability isn't required at all (though strongly recommended). Every other state requires at least state-minimum liability limits.",
      },
      {
        q: "Will my lender know if I drop full coverage?",
        a: "Yes. Lenders are listed on your policy as 'lienholders' and receive cancellation notices. Dropping full coverage on a financed car triggers force-placed insurance (typically 3–5x more expensive).",
      },
    ],
  },
  {
    slug: "dealer-financing-vs-credit-union",
    title: "Dealer Financing vs. Credit Union",
    h1: "Dealer Financing vs. Credit Union Auto Loans: Where Do You Really Save?",
    description:
      "Dealer finance is convenient. Credit unions are cheap. Here's the actual rate gap, the catch, and which to use when.",
    category: "Loans",
    read_time: "6 min read",
    related_tool: "auto-loan-calculator",
    a: {
      name: "Dealer Financing",
      tagline: "Sign here. Drive home tonight.",
      monthly: "Higher (1–3% APR markup typical)",
      term: "Whatever the dealer pushes (often 72–84 mo)",
      ownership: "Yours at payoff",
      mileage: "N/A",
      maintenance: "Add-ons easy to roll in (GAP, warranty)",
      upfront: "Dealer arranges everything in one stop",
      end_of_term: "Loan paid, car owned",
      pros: [
        "One-stop convenience",
        "Manufacturer 0% promo rates on select models",
        "Same-day approval common",
        "Can negotiate rate against pre-approval",
      ],
      cons: [
        "Average 1.4-point rate markup over wholesale",
        "F&I office upsells $2,000–$5,000 in add-ons",
        "Longer terms hide higher rates",
        "Lender choice limited to dealer partners",
      ],
    },
    b: {
      name: "Credit Union Loan",
      tagline: "Member-owned. Margin matters less.",
      monthly: "Lower (typically 0.5–1.5% APR below banks)",
      term: "36 – 84 months, you pick",
      ownership: "Yours at payoff",
      mileage: "N/A",
      maintenance: "You add GAP/warranty separately",
      upfront: "Apply online, get pre-approved letter",
      end_of_term: "Loan paid, car owned",
      pros: [
        "Lowest APRs in the industry on average",
        "Pre-approval lets you negotiate as a cash buyer",
        "Member-friendly hardship policies",
        "No hidden F&I fees or upsells",
      ],
      cons: [
        "Must qualify for membership (most are easy)",
        "Application takes 1–3 days",
        "Online portal sometimes clunky",
        "No 0% promo rates",
      ],
    },
    table: [
      { feature: "Avg. APR (720 FICO, new)", a: "7.4%", b: "5.9%" },
      { feature: "Avg. APR (650 FICO, used)", a: "12.3%", b: "9.8%" },
      { feature: "Add-on pressure", a: "High", b: "None" },
      { feature: "Approval speed", a: "Same-day", b: "1–3 days" },
      { feature: "Pre-approval available", a: "Sometimes", b: "Always" },
      { feature: "Loan term flexibility", a: "Long (72–84mo) pushed", b: "You choose" },
      { feature: "Best for", a: "0% promo deals", b: "Everyone else" },
    ],
    winner: {
      cu: "Almost always your default. Get pre-approved from a credit union, then let the dealer try to beat it. If they can't, you keep the credit union loan.",
      dealer: "Only when the manufacturer is running a genuine 0% / 1.9% subvented APR promo and you'd qualify — those rates are subsidized and unbeatable.",
    },
    faqs: [
      {
        q: "How much does dealer financing actually cost extra?",
        a: "On a $30k loan, a 1.4-point rate markup costs ~$1,250 over 5 years. F&I add-ons (GAP, warranty, paint, etheric) can stack another $2,000–$5,000 onto the financed balance.",
      },
      {
        q: "Can I refinance dealer financing into a credit union loan?",
        a: "Yes — and it's one of the most common refi scenarios. Many borrowers do this 60–90 days after purchase once the dealer's grace period ends.",
      },
      {
        q: "Are all credit unions easy to join?",
        a: "Most. PenFed, Navy Federal (if eligible), Alliant, Consumers Credit Union, and many others have nationwide membership through a $5–$15 one-time donation or association membership.",
      },
    ],
  },
  {
    slug: "buy-here-pay-here-vs-bank-loan",
    title: "Buy Here Pay Here vs. Bank Loan",
    h1: "Buy Here Pay Here vs. Bank Auto Loan: The Real Cost of 'Easy Approval'",
    description:
      "Buy Here Pay Here lots promise instant approval with bad credit. Here's what the loans actually cost — and the alternatives nobody tells you about.",
    category: "Loans",
    read_time: "7 min read",
    related_tool: "auto-loan-calculator",
    a: {
      name: "Buy Here Pay Here",
      tagline: "Approved at the lot. Pay weekly.",
      monthly: "$280 – $480 (weekly: $80–$130)",
      term: "24 – 48 months on older cars",
      ownership: "Title held until full payoff",
      mileage: "N/A",
      maintenance: "On you — cars often higher-mileage",
      upfront: "Down payment $500–$2,500",
      end_of_term: "Title released if all payments made",
      pros: [
        "Same-day approval with bad/no credit",
        "No outside lender approval needed",
        "Drive off the lot today",
        "Sometimes reports to credit bureaus",
      ],
      cons: [
        "Average APR 20%+ (some states 29.99% cap)",
        "Cars priced 20–40% above wholesale",
        "GPS kill switches common",
        "Repossessions trigger fast (10–30 days late)",
      ],
    },
    b: {
      name: "Bank / Subprime Lender",
      tagline: "Higher hurdle. Far lower cost.",
      monthly: "$220 – $410",
      term: "48 – 72 months typical",
      ownership: "Yours at payoff",
      mileage: "N/A",
      maintenance: "Standard",
      upfront: "Down payment varies, often 0–10%",
      end_of_term: "Title released, no GPS, no kill switch",
      pros: [
        "Subprime APRs 10–18% (vs. 20%+ BHPH)",
        "Vehicle priced at market, not marked up",
        "Reports to all 3 credit bureaus",
        "Refinance ladder once credit improves",
      ],
      cons: [
        "Requires FICO 500+ at most lenders",
        "Approval takes 1–3 days",
        "Down payment / cosigner sometimes needed",
        "Stricter income verification",
      ],
    },
    table: [
      { feature: "Typical APR", a: "20% – 29.99%", b: "10% – 18%" },
      { feature: "Avg. price markup over wholesale", a: "20–40%", b: "0–5%" },
      { feature: "Credit reporting", a: "Inconsistent", b: "All 3 bureaus" },
      { feature: "Repossession trigger", a: "10–30 days late", b: "60–90 days late" },
      { feature: "GPS tracker / kill switch", a: "Common", b: "Rare" },
      { feature: "Min credit score", a: "None", b: "500–550" },
      { feature: "Refinance later", a: "Hard (overpriced collateral)", b: "Easy after 12 months" },
    ],
    winner: {
      bhph: "Almost never — and only when no subprime lender (RoadLoans, Carvana Financing, AutoCredit Express) will approve you AND you absolutely need a car this week.",
      bank: "Every other scenario. Even at 18% APR with a subprime lender, you save thousands vs. BHPH and start the path to credit recovery.",
    },
    faqs: [
      {
        q: "Can I get an auto loan with a 500 credit score?",
        a: "Yes. Subprime specialists like Westlake Financial, RoadLoans, and Carvana's in-house financing routinely approve FICOs 500–580. APRs are high (14–22%) but materially below BHPH.",
      },
      {
        q: "Do Buy Here Pay Here loans build credit?",
        a: "Only if the dealer reports — and many don't. Always ask: 'Do you report to Equifax, Experian, and TransUnion?' If the answer isn't all three, your payments aren't building credit.",
      },
      {
        q: "How do I escape a Buy Here Pay Here loan?",
        a: "Pay on time for 6–12 months to rebuild credit, then refinance with a credit union or subprime bank lender. Most BHPH cars are overpriced — you may need positive equity (or a small cash-in) to refinance successfully.",
      },
    ],
  },
  // ====================================================================
  // Iter-69 — 6 new comparisons closing gaps in the Compare ecosystem.
  // Categories: Refinance (2) · Insurance (2) · Loans (1) · Warranty (1)
  // ====================================================================
  {
    slug: "refinance-vs-keep-loan",
    title: "Refinance vs. Keep Current Loan",
    h1: "Refinance vs. Keep Your Current Auto Loan: When Refi Actually Pays",
    description:
      "Most drivers leave money on the table by sticking with their original loan. Here's the math on when refinancing wins, when it doesn't, and the break-even rules.",
    category: "Refinance",
    read_time: "6 min read",
    related_tool: "auto-loan-refinance-calculator",
    a: {
      name: "Refinance",
      tagline: "Replace the loan, keep the car.",
      monthly: "$45–$120/mo lower typical",
      term: "Often shortens 6–18 months",
      ownership: "Same car, new lender",
      mileage: "No change — your car, your rules",
      maintenance: "Unchanged from current ownership",
      upfront: "$0–$75 (state title-transfer only)",
      end_of_term: "Pay off the new loan, own the car",
      pros: [
        "Lower APR if credit improved or rates dropped",
        "Lower monthly payment OR shorter term",
        "Soft credit pull for pre-qual — no score damage",
        "Most national refi lenders charge $0 origination",
      ],
      cons: [
        "Closing window typically 30–90 days",
        "Slight 3–5 point credit dip from new tradeline",
        "Doesn't help if you're underwater (LTV >100%)",
        "Time investment — pre-qual + paperwork ~30 min",
      ],
    },
    b: {
      name: "Keep Current Loan",
      tagline: "Stay the course.",
      monthly: "Whatever you're paying today",
      term: "Whatever you have left",
      ownership: "Same car, same lender",
      mileage: "No change",
      maintenance: "Unchanged",
      upfront: "$0",
      end_of_term: "Pay off, own the car",
      pros: [
        "Zero effort — autopay continues",
        "No new tradeline on credit report",
        "Predictable — same payment, same lender",
        "Makes sense if you're <12 months from payoff",
      ],
      cons: [
        "Potentially leaving $700–$1,800 in interest savings",
        "Dealer-loan markup never goes away unless you refi",
        "Higher monthly than necessary if rates dropped",
        "Compounding loyalty cost over 4–6 year terms",
      ],
    },
    table: [
      { feature: "Effort to evaluate", a: "30 min (soft pull)", b: "Zero" },
      { feature: "Typical lifetime savings", a: "$700–$1,800", b: "$0" },
      { feature: "Monthly impact", a: "−$45–$120/mo typical", b: "Unchanged" },
      { feature: "Credit impact", a: "−3–5 pts, recovers ~90 days", b: "None" },
      { feature: "Best when credit improved ≥50 pts", a: "Strong win", b: "Leaving money on table" },
      { feature: "Best when <12 mo to payoff", a: "Math is borderline", b: "Easier win" },
      { feature: "Best when underwater on loan", a: "Most lenders decline", b: "Wait it out" },
      { feature: "Setup cost", a: "$0–$75 title fee", b: "$0" },
    ],
    winner: {
      refinance:
        "Your credit improved 50+ points since signing, OR market rates dropped 1+ points, OR you took a dealer loan 6+ months ago and have established payment history. Any one of those almost guarantees the refi math works.",
      keep:
        "You're inside 12 months of payoff (savings don't cover the setup hassle), you're underwater on the loan, or your current APR is already at the market floor for your credit tier.",
    },
    faqs: [
      {
        q: "How much do I need to save monthly to make refinancing worth it?",
        a: "Use the break-even rule: monthly savings ÷ any new-lender fees = break-even months. Most refi lenders charge $0 origination — only your state's $5–$75 title-transfer fee applies. That means break-even is usually under 3 months, and everything past that is pure savings.",
      },
      {
        q: "Will refinancing hurt my credit score?",
        a: "Pre-qualification is a soft credit pull — zero impact. Only the lender you actually accept runs a hard inquiry. The new tradeline can ding your score 3–5 points short-term but typically recovers within 90 days.",
      },
      {
        q: "Can I refinance an auto loan I just took out?",
        a: "Yes, but most lenders require 6+ months of payment history on the existing loan before they'll refinance it. Some specialists (LightStream, OpenRoad) will refi as early as 60 days for excellent-credit borrowers.",
      },
    ],
  },
  {
    slug: "bank-vs-online-refinance",
    title: "Bank vs. Online Refinance",
    h1: "Bank vs. Online Auto Refinance: Which Is Cheaper in 2026?",
    description:
      "Traditional brick-and-mortar bank refi vs. online-first refi platforms. Where the rate floors actually sit, fee comparisons, and which path wins for your credit tier.",
    category: "Refinance",
    read_time: "5 min read",
    related_tool: "auto-loan-refinance-calculator",
    a: {
      name: "Traditional Bank",
      tagline: "Your existing relationship bank.",
      monthly: "$415 typical on a $20k refi",
      term: "36–72 months standard",
      ownership: "Same car, paid-off old loan",
      mileage: "—",
      maintenance: "—",
      upfront: "$0–$50 (some banks charge an app fee)",
      end_of_term: "Owned outright",
      pros: [
        "Existing relationship may unlock 0.25–0.5 pt discount",
        "Face-to-face support if anything goes wrong",
        "Branch access for cashier's check / wire if needed",
        "Often better for $50k+ luxury vehicle refi",
      ],
      cons: [
        "Rate floors typically 0.5–1.5 pts higher than online",
        "Slower approval — often 5–10 business days",
        "Many big banks (Chase, Wells, BofA) don't even offer auto refi anymore",
        "Branch hours limit when you can talk to someone",
      ],
    },
    b: {
      name: "Online Refi Platform",
      tagline: "LightStream, AutoPay, RateGenius, OpenRoad.",
      monthly: "$385 typical on a $20k refi",
      term: "36–84 months — longer terms available",
      ownership: "Same car, paid-off old loan",
      mileage: "—",
      maintenance: "—",
      upfront: "$0 — universally no application or origination fee",
      end_of_term: "Owned outright",
      pros: [
        "Lower rate floors (0.5–1.5 pts cheaper typical)",
        "Faster approval — pre-qual in about 2 minutes, funding in 3–7 days",
        "Soft credit pull for pre-qual (universal)",
        "Multi-lender marketplaces (AutoPay) auto-shop 10+ lenders for you",
      ],
      cons: [
        "No physical branch if anything goes sideways",
        "Customer support is chat/phone-only",
        "Most marketplaces don't underwrite directly — they route to partners",
        "Identity verification + document upload required online",
      ],
    },
    table: [
      { feature: "Typical APR floor (good credit)", a: "7.4%", b: "5.9%" },
      { feature: "Application fee", a: "$0–$50", b: "$0 universal" },
      { feature: "Origination fee", a: "$0–$200", b: "$0 universal" },
      { feature: "Pre-qual to funding", a: "5–10 business days", b: "3–7 business days" },
      { feature: "Soft pull pre-qual", a: "Some yes / some no", b: "Universal yes" },
      { feature: "Best credit tier", a: "740+ luxury vehicles", b: "All credit tiers" },
      { feature: "Branch support", a: "Yes", b: "No — chat/phone only" },
      { feature: "Shopping multiple lenders", a: "You shop manually", b: "Marketplace auto-shops" },
    ],
    winner: {
      bank:
        "You have a luxury vehicle ($50k+), you genuinely use your bank's branch network, or you've banked there 10+ years and qualify for a relationship discount that closes the rate gap.",
      online:
        "Almost everyone else. Online refi platforms have lower rate floors, $0 fees, and pre-qual in about 2 minutes — there's almost no scenario where a traditional bank wins on price for a sub-$50k auto refi.",
    },
    faqs: [
      {
        q: "Is online refinance safe?",
        a: "Yes — major platforms (LightStream, AutoPay, RateGenius, OpenRoad) are licensed lenders or licensed marketplace brokers, fully regulated by state DMVs and consumer-finance bodies. Use HTTPS, verify the URL, and never wire money outside the platform.",
      },
      {
        q: "Do online refi platforms underwrite directly or just refer me?",
        a: "Mixed — LightStream, OpenRoad, and Caribou underwrite directly. AutoPay and RateGenius are marketplaces that route your application to 10+ partner lenders. Both models work; the marketplace approach typically surfaces the lowest rate but adds a hand-off step.",
      },
      {
        q: "Why don't big banks offer auto refinance anymore?",
        a: "Many large national banks (Chase, Wells Fargo, Bank of America) exited the auto refinance market between 2019–2023 because margins are thin and the operations don't scale well alongside their mortgage and credit-card businesses. Credit unions and online-first platforms picked up the volume.",
      },
    ],
  },
  {
    slug: "high-vs-low-deductible-insurance",
    title: "High vs. Low Deductible",
    h1: "High vs. Low Deductible Auto Insurance: Which Saves You More?",
    description:
      "Your deductible is the single biggest lever on your premium. Here's the math on $500 vs $1,000 vs $2,000 deductibles, and the emergency-fund rule that picks the right one.",
    category: "Insurance",
    read_time: "5 min read",
    related_tool: "auto-insurance-cost-estimator",
    a: {
      name: "High Deductible ($1,000–$2,500)",
      tagline: "Lower premium, more skin in the game.",
      monthly: "$118/mo typical",
      term: "Annual policy",
      ownership: "—",
      mileage: "—",
      maintenance: "—",
      upfront: "$1,000+ out of pocket at claim time",
      end_of_term: "—",
      pros: [
        "20–30% lower premium vs. low deductible",
        "Discourages small claims that raise renewal rates",
        "Best math if you have a 6-month emergency fund",
        "Compounds — savings stack every year you stay claim-free",
      ],
      cons: [
        "Painful out-of-pocket if claim hits",
        "Requires real savings discipline",
        "Bad fit for unreliable older cars with high claim risk",
        "Bumper-fender benders may be cheaper to fix yourself",
      ],
    },
    b: {
      name: "Low Deductible ($250–$500)",
      tagline: "Higher premium, smaller out-of-pocket.",
      monthly: "$152/mo typical",
      term: "Annual policy",
      ownership: "—",
      mileage: "—",
      maintenance: "—",
      upfront: "$250–$500 at claim time",
      end_of_term: "—",
      pros: [
        "Easier to absorb claim cost out-of-pocket",
        "Better fit if you have no emergency fund",
        "Lower friction at claim time — fewer 'should I file?' debates",
        "Standard recommendation for first-time policyholders",
      ],
      cons: [
        "20–30% higher premium",
        "Encourages filing small claims that hurt renewal rates",
        "Pays for itself only if you average 1+ claims every 4 years",
        "Insurance company collects the difference as profit when you don't claim",
      ],
    },
    table: [
      { feature: "Typical premium delta", a: "−20% to −30%", b: "Baseline" },
      { feature: "Annual savings", a: "$300–$500", b: "$0" },
      { feature: "Out-of-pocket at claim", a: "$1,000–$2,500", b: "$250–$500" },
      { feature: "Break-even years (claim-free)", a: "2–5 years", b: "—" },
      { feature: "Best for ages 25–55 w/ savings", a: "Strong win", b: "Costs more for similar protection" },
      { feature: "Best for new drivers / no savings", a: "Risky", b: "Safer choice" },
      { feature: "Best for old reliable car", a: "Strong win", b: "Probably overpaying" },
      { feature: "Best for high-claim-risk areas", a: "Borderline", b: "Wins on peace of mind" },
    ],
    winner: {
      high:
        "You have a 6-month emergency fund, you've gone 4+ years without filing a claim, AND you drive a reliable car (Honda, Toyota, Subaru). The premium savings compound into real money over time.",
      low:
        "You're a new driver, you have no emergency fund, or you're in a high-claim-risk environment (hail-prone state, theft-prone area, high-deer collision zones).",
    },
    faqs: [
      {
        q: "What's the most popular deductible?",
        a: "$500 is the U.S. standard — about 56% of policies use it. $1,000 is rising fast (~28%) as drivers wise up to the premium savings. $250 deductibles are now under 10% of policies and typically only chosen by drivers without emergency funds.",
      },
      {
        q: "If I raise my deductible mid-policy, do I owe the difference at claim time?",
        a: "Yes — the deductible that applies is whatever was in effect on the date of the claim event. Raise your deductible at renewal to avoid mid-term confusion.",
      },
      {
        q: "Does deductible affect liability coverage too?",
        a: "No — deductibles only apply to collision and comprehensive coverage (damage to your car). Liability claims (damage you cause to others) have no deductible. That's why dropping unnecessary collision on an old car can save more than tweaking deductibles.",
      },
    ],
  },
  {
    slug: "state-minimum-vs-full-coverage",
    title: "State Minimum vs. Full Coverage",
    h1: "State Minimum vs. Full Coverage Insurance: When Each One Wins",
    description:
      "State-minimum liability is the cheapest legal option — but it's underprotection for most drivers. The 10× rule that decides whether you should pay for full coverage.",
    category: "Insurance",
    read_time: "6 min read",
    related_tool: "auto-insurance-cost-estimator",
    a: {
      name: "State Minimum (Liability-Only)",
      tagline: "Legal floor. Lowest premium.",
      monthly: "$58/mo typical",
      term: "Annual policy",
      ownership: "—",
      mileage: "—",
      maintenance: "—",
      upfront: "$0 — but everything if YOUR car is totaled",
      end_of_term: "—",
      pros: [
        "Cheapest legal way to drive — 60–70% less premium",
        "Reasonable for low-value cars (<$3,000 market value)",
        "Saves $1,000+/yr vs. full coverage in most states",
        "Best fit when you can self-insure the car",
      ],
      cons: [
        "$0 paid out if YOU damage YOUR car",
        "$0 paid for theft, fire, hail, vandalism",
        "Lender requires full coverage if you're financing",
        "State minimums are often dangerously low (FL: $10k bodily injury)",
      ],
    },
    b: {
      name: "Full Coverage (Liability + Collision + Comp)",
      tagline: "Protect everyone, including yourself.",
      monthly: "$148/mo typical",
      term: "Annual policy",
      ownership: "—",
      mileage: "—",
      maintenance: "—",
      upfront: "Your deductible only ($500–$2,000)",
      end_of_term: "—",
      pros: [
        "Pays out if YOU total your own car",
        "Covers theft, fire, hail, flood, vandalism",
        "Lender requires it on financed vehicles",
        "Standard limits (100/300/100) actually protect your assets",
      ],
      cons: [
        "60–70% more expensive than state minimum",
        "Often overkill on old cars worth <$5,000",
        "10× rule says drop it when premium > 10% of car value",
        "Most policies still include $500 deductible on top",
      ],
    },
    table: [
      { feature: "Typical annual premium", a: "$696", b: "$1,776" },
      { feature: "Bodily injury limit", a: "$15k–$30k (state min)", b: "$100k–$300k recommended" },
      { feature: "Damage to YOUR car", a: "$0", b: "ACV minus deductible" },
      { feature: "Theft/fire/vandalism", a: "$0", b: "Covered" },
      { feature: "Lender accepts", a: "Only on paid-off cars", b: "Yes — required if financed" },
      { feature: "Asset protection (lawsuit)", a: "Dangerously thin", b: "Realistic" },
      { feature: "Best for car worth <$3k", a: "Strong win", b: "Overpaying" },
      { feature: "Best for car worth $15k+", a: "Underprotected", b: "Right fit" },
    ],
    winner: {
      minimum:
        "Your car is worth less than $3,000 (KBB private-party value), you own it outright, AND you have $5,000+ in savings to absorb a total-loss scenario.",
      full:
        "You're financing or leasing the car (lender requires it), your car is worth $5,000+, or you couldn't write a check for a replacement car tomorrow. Bump liability limits to 100/300/100 — minimum-limits exposure to a real lawsuit is the biggest financial risk most drivers carry.",
    },
    faqs: [
      {
        q: "What is the 10× rule for dropping full coverage?",
        a: "If your annual premium for collision + comp is more than 10% of your car's current market value, dropping to liability-only is the cheaper expected-value play. Example: $900/yr in collision+comp on a $7,500 car = 12%, so dropping makes sense.",
      },
      {
        q: "Can I drive legally with no insurance?",
        a: "No — every U.S. state except New Hampshire requires at least liability insurance. NH requires drivers to prove they can pay damages they cause, which most drivers do by buying insurance anyway. Driving uninsured carries fines, license suspension, and personal liability for any damage you cause.",
      },
      {
        q: "Are state minimums enough to protect my assets?",
        a: "Rarely. Florida's $10k bodily injury minimum is exhausted by a single ER visit. California's $15k/$30k won't cover a multi-car accident. If you have any assets to protect (home equity, retirement savings), the standard recommendation is 100/300/100 — and an umbrella policy past $500k net worth.",
      },
    ],
  },
  {
    slug: "short-term-vs-long-term-auto-loan",
    title: "Short-Term vs. Long-Term Auto Loan",
    h1: "Short-Term (36–48 mo) vs. Long-Term (72–84 mo) Auto Loan: Which Costs Less?",
    description:
      "84-month loans drop your payment but cost $3,000–$6,000 more in interest. Here's the real lifetime cost math and when each term length actually makes sense.",
    category: "Loans",
    read_time: "6 min read",
    related_tool: "auto-loan-calculator",
    a: {
      name: "Short-Term (36–48 mo)",
      tagline: "Higher payment, lower lifetime cost.",
      monthly: "$615/mo on $25k @ 7.5% / 48-mo",
      term: "36–48 months",
      ownership: "Own free and clear sooner",
      mileage: "—",
      maintenance: "Inside factory warranty most of loan",
      upfront: "Standard down payment + tax/title",
      end_of_term: "Paid off → 4–8 years of payment-free driving",
      pros: [
        "Lower total interest by $2,000–$6,000",
        "Builds equity fast — escape underwater faster",
        "Lenders offer 0.5–1.5 pts lower APR on shorter terms",
        "Cleaner exit when you sell or trade",
      ],
      cons: [
        "Payment is 35–55% higher month-to-month",
        "Squeezes monthly budget",
        "Requires more cash flow flexibility",
        "Less room for life-event payment relief",
      ],
    },
    b: {
      name: "Long-Term (72–84 mo)",
      tagline: "Lower payment, higher lifetime cost.",
      monthly: "$435/mo on $25k @ 8.5% / 84-mo",
      term: "72–84 months",
      ownership: "Own free and clear in 6–7 years",
      mileage: "—",
      maintenance: "Out of factory warranty for last 3–4 years of loan",
      upfront: "Same down payment",
      end_of_term: "Paid off — car often near end of useful life",
      pros: [
        "Payment fits tighter budgets",
        "Buys you a more expensive car for the same monthly",
        "Cash flow flexibility for emergencies",
        "Common for SUVs and trucks where total prices are higher",
      ],
      cons: [
        "$3,000–$6,000 more total interest",
        "APR is 0.5–1.5 points higher (lenders price in risk)",
        "Underwater for 30–48 months — total loss = gap insurance required",
        "Paying for a car after warranty expires — repair bills + payments overlap",
      ],
    },
    table: [
      { feature: "Monthly payment ($25k @ 7.5%)", a: "$615 (48-mo)", b: "$435 (84-mo)" },
      { feature: "Total interest paid", a: "$4,520", b: "$8,540" },
      { feature: "Typical APR offered", a: "6.9–7.5%", b: "7.9–9.0%" },
      { feature: "Months underwater (LTV >100%)", a: "12–18 months", b: "36–48 months" },
      { feature: "Inside factory warranty", a: "Full term", b: "Last 3–4 yrs uncovered" },
      { feature: "Gap insurance needed", a: "Optional", b: "Strongly recommended" },
      { feature: "Best for budget flexibility", a: "Painful", b: "Easier" },
      { feature: "Best for lifetime cost", a: "Strong win", b: "Costly" },
    ],
    winner: {
      short:
        "Your monthly budget has room for the higher payment AND you want the lowest lifetime cost. Building equity fast also means you escape underwater status sooner — which matters if you might sell or trade in the next 3 years.",
      long:
        "You're stretching to afford the car you actually need (commercial-use truck, large-family SUV) and a 60-month payment genuinely doesn't fit. Pair the long term with aggressive principal prepayments — most lenders allow them without penalty.",
    },
    faqs: [
      {
        q: "Is a 60-month loan a good compromise?",
        a: "Yes — 60 months is the most common term in the U.S. for a reason. It's the sweet spot between manageable payment and reasonable lifetime cost. Roughly 38% of all new-car loans are 60-month. If 60 fits your budget, it's almost always the right pick.",
      },
      {
        q: "Should I take an 84-month loan if it's the only way to afford the car?",
        a: "Only if you genuinely need that specific car (truck for work, family-hauler for a growing family). If the 84-month term is just so you can buy a fancier car, you're underwriting your own future financial pressure. Better to buy a less-expensive vehicle at 60 months.",
      },
      {
        q: "Can I refinance a long-term loan into a shorter one later?",
        a: "Yes — refinancing into a shorter term is a common 'catch-up' play once your credit improves or rates drop. Just watch the math: a shorter term at a lower APR almost always saves money; a shorter term at the same APR just raises your payment.",
      },
    ],
  },
  {
    slug: "manufacturer-vs-third-party-warranty",
    title: "Manufacturer vs. Third-Party Warranty",
    h1: "Manufacturer Extended Warranty vs. Third-Party: Which Is Worth Buying?",
    description:
      "Dealer F&I sells the manufacturer warranty at 200–400% markup. Third-party providers (Endurance, CarShield, Olive) underwrite identical coverage for less. The hidden tradeoffs.",
    category: "Warranty",
    read_time: "7 min read",
    related_tool: "extended-warranty-decision-tree",
    a: {
      name: "Manufacturer Extended Warranty",
      tagline: "OEM-backed. Sold at the dealer F&I desk.",
      monthly: "$3,800 lump sum or rolled into loan",
      term: "Up to 100k miles / 7 years from in-service date",
      ownership: "—",
      mileage: "—",
      maintenance: "Repairs at OEM dealerships only",
      upfront: "Big — often $2,200–$4,500 if not financed",
      end_of_term: "Coverage ends, vehicle still yours",
      pros: [
        "OEM parts + factory-trained techs only",
        "Coverage matches your original new-car warranty",
        "No coverage disputes — same underwriter as factory",
        "Easy claim process at any same-brand dealer",
      ],
      cons: [
        "Dealer F&I marks it up 200–400%",
        "Only valid at same-brand dealer service depts",
        "Lump-sum cost is steep when paid out of pocket",
        "Often cancellable in first 30 days for full refund (use it!)",
      ],
    },
    b: {
      name: "Third-Party Warranty (Endurance, CarShield, Olive)",
      tagline: "Independent administrator. Bought direct.",
      monthly: "$1,500–$2,200 lump or $80–$140/mo",
      term: "Up to 200k miles / 15 years available",
      ownership: "—",
      mileage: "—",
      maintenance: "Any ASE-certified shop typically",
      upfront: "Smaller lump or monthly billing",
      end_of_term: "Coverage ends, vehicle still yours",
      pros: [
        "40–60% cheaper than dealer F&I",
        "Repairs at any ASE-certified shop (more convenience)",
        "Longer coverage windows available (15-yr plans exist)",
        "Monthly billing option spreads the cost",
      ],
      cons: [
        "Quality varies widely by provider — vet ratings carefully",
        "Some providers (esp. unrated) have high claim-denial rates",
        "Shop must accept your provider — verify before signing",
        "Coverage exclusions differ from OEM — read fine print",
      ],
    },
    table: [
      { feature: "Typical cost (5 yr / 75k coverage)", a: "$3,800", b: "$1,600" },
      { feature: "Markup over wholesale", a: "200–400%", b: "30–60%" },
      { feature: "Where repairs covered", a: "OEM dealer only", b: "Any ASE shop typically" },
      { feature: "Underwriter", a: "Manufacturer", b: "AmSh / Royal Adm / ASC etc" },
      { feature: "Mileage cap", a: "100k typical", b: "Up to 200k available" },
      { feature: "Time cap", a: "7 yrs from in-service", b: "Up to 15 yrs available" },
      { feature: "Payment options", a: "Lump or rolled into loan", b: "Lump OR monthly" },
      { feature: "Cancelable first 30 days", a: "Yes (full refund)", b: "Yes (full refund)" },
    ],
    winner: {
      manufacturer:
        "You drive a luxury European brand (BMW, Mercedes, Audi, Porsche) where OEM-trained service genuinely matters AND the dealer F&I price is within 30% of the third-party equivalent. Negotiate hard — F&I sales staff have ~$1,500 in markup to give back.",
      thirdparty:
        "Almost every other scenario. Mainstream brands (Honda, Toyota, Ford, GM) don't require OEM service for warranty repairs, and third-party providers underwrite the same risk for materially less money. Endurance, Olive, and Protect My Car consistently rank highest for claim approval.",
    },
    faqs: [
      {
        q: "Are third-party warranties a scam?",
        a: "Not categorically — but quality varies wildly. Reputable providers (Endurance, Olive, Protect My Car, CARCHEX) have BBB A+ ratings and reasonable claim-approval rates. Avoid providers with high BBB complaint counts, unverified addresses, or unsolicited robocall sales. The 'extended car warranty robocall' meme is real and refers to a small subset of bad-actor providers.",
      },
      {
        q: "Can I buy a manufacturer warranty after I leave the dealer?",
        a: "Yes — most OEMs let you buy the extended manufacturer warranty up to a year after purchase, often online without dealer F&I markup. This is the loophole: skip the F&I pitch, buy the same warranty directly from the manufacturer 30–60 days later for 30–50% less.",
      },
      {
        q: "Does my new car already have a warranty? Why would I need another?",
        a: "Yes — every new car has a factory bumper-to-bumper (typically 3 yr / 36k mi) and powertrain (5–10 yr / 60–100k mi). The extended warranty starts when these expire. The decision tree is: 'Will I keep the car past the factory warranty?' If yes AND the car brand has high out-of-warranty repair costs, extended coverage makes sense.",
      },
    ],
  },
];

export const findComparison = (slug) => COMPARISONS.find((c) => c.slug === slug);

// Iter-196.DD — Append 30 carrier-vs-carrier comparisons after the
// hand-written editorial set. Order intentional: hand-written
// decision comparisons retain their authoring weight at the top of
// any iteration over COMPARISONS; carrier-vs-carrier comparisons
// extend the tail.
COMPARISONS.push(...generateCarrierVsComparisons());
